// @ts-nocheck
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';

/**
 * DocumentCleanupContext
 *
 * Allows deeply nested MultiDocumentFieldComponent instances to register
 * their cleanup functions with a parent dialog (e.g. ConfigColumnRowDialog).
 * This avoids threading refs through BasicDetails → MultiEditAttributeField
 * → AttributeField → MultiDocumentFieldComponent.
 *
 * Usage:
 * - Parent: Wrap children with <DocumentCleanupProvider cleanupRef={cleanupRef}>
 * - Parent: Call cleanupRef.current?.cleanupAllDocuments() on cancel
 * - Child: Call registerCleanup(attrId, cleanupFn) on mount
 * - Child: Call unregisterCleanup(attrId) on unmount
 */
const DocumentCleanupContext = createContext(null);

export const useDocumentCleanup = () => useContext(DocumentCleanupContext);

export const DocumentCleanupProvider = ({ children, cleanupRef }) => {
  const cleanupFnsRef = useRef({});

  const registerCleanup = useCallback((attrId, cleanupFn) => {
    cleanupFnsRef.current[attrId] = cleanupFn;
  }, []);

  const unregisterCleanup = useCallback((attrId) => {
    delete cleanupFnsRef.current[attrId];
  }, []);

  const cleanupAllDocuments = useCallback(async () => {
    const promises = Object.values(cleanupFnsRef.current).map((fn) =>
      Promise.resolve()
        .then(() => fn())
        .catch((err) => {
          console.error('Document cleanup error:', err);
        })
    );
    await Promise.allSettled(promises);
    cleanupFnsRef.current = {};
  }, []);

  useEffect(() => {
    if (!cleanupRef) return undefined;

    const value = { cleanupAllDocuments };
    if (typeof cleanupRef === 'function') {
      cleanupRef(value);
    } else {
      // eslint-disable-next-line no-param-reassign
      cleanupRef.current = value;
    }

    return () => {
      if (typeof cleanupRef === 'function') {
        cleanupRef(null);
      } else {
        // eslint-disable-next-line no-param-reassign
        cleanupRef.current = null;
      }
    };
  }, [cleanupRef, cleanupAllDocuments]);

  return (
    <DocumentCleanupContext.Provider
      value={{ registerCleanup, unregisterCleanup, cleanupAllDocuments }}
    >
      {children}
    </DocumentCleanupContext.Provider>
  );
};

DocumentCleanupProvider.propTypes = {
  children: PropTypes.node.isRequired,
  cleanupRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.shape({}) }),
  ]),
};

DocumentCleanupProvider.defaultProps = {
  cleanupRef: null,
};

export default DocumentCleanupContext;
